// src/pages/HomeComisiones.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  getPendientesComisiones,
  getLiquidacionesComisiones,
  getPagosComisiones,
  getResumenSemanalComisiones,
} from "../helpers/rutaComisiones";

import "../css/homealtas.css";

/* =========================================================
   ICONOS
   ========================================================= */

const Icon = ({ type }) => {
  const common = {
    className: "homealtas-icon-svg",
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": "true",
  };

  const strokeProps = {
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  switch (type) {
    case "administracion":
      return (
        <svg {...common}>
          <circle
            {...strokeProps}
            cx="8"
            cy="8"
            r="3"
          />

          <circle
            {...strokeProps}
            cx="16"
            cy="16"
            r="3"
          />

          <path
            {...strokeProps}
            d="M7 17 17 7"
          />
        </svg>
      );

    case "informes":
      return (
        <svg {...common}>
          <path
            {...strokeProps}
            d="M4 19V9"
          />

          <path
            {...strokeProps}
            d="M10 19V5"
          />

          <path
            {...strokeProps}
            d="M16 19v-7"
          />

          <path
            {...strokeProps}
            d="M22 19V3"
          />
        </svg>
      );

    default:
      return (
        <svg {...common}>
          <circle
            {...strokeProps}
            cx="12"
            cy="12"
            r="9"
          />
        </svg>
      );
  }
};

/* =========================================================
   FORMATOS
   ========================================================= */

const formatoUSD = (valor) => {
  return Number(
    valor || 0
  ).toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const formatoFecha = (valor) => {
  if (!valor) {
    return "-";
  }

  const texto =
    String(valor);

  const match =
    texto.match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }

  const fecha =
    new Date(valor);

  if (
    Number.isNaN(
      fecha.getTime()
    )
  ) {
    return "-";
  }

  return fecha.toLocaleDateString(
    "es-AR"
  );
};

const nombreVendedor = (
  liquidacion
) => {
  const vendedor =
    liquidacion?.vendedor;

  if (!vendedor) {
    return "-";
  }

  return (
    [
      vendedor.nombres,
      vendedor.apellidos,
    ]
      .filter(Boolean)
      .join(" ") ||
    vendedor.email ||
    "-"
  );
};

/* =========================================================
   HOME
   ========================================================= */

const HomeComisiones = () => {
  const [cargando, setCargando] =
    useState(true);

  const [error, setError] =
    useState("");

  const [sedeDashboard, setSedeDashboard] =
    useState("");

  const [
    pendientesValidar,
    setPendientesValidar,
  ] = useState(0);

  const [
    semanaPendienteUSD,
    setSemanaPendienteUSD,
  ] = useState(0);

  const [
    liquidaciones,
    setLiquidaciones,
  ] = useState([]);

  const [
    resumenLiquidaciones,
    setResumenLiquidaciones,
  ] = useState({
    cantidad: 0,
    totalLiquidadoUSD: 0,
    totalPagadoUSD: 0,
    saldoPendienteUSD: 0,
  });

  const [
    resumenPagos,
    setResumenPagos,
  ] = useState({
    cantidadPagos: 0,
    totalPagadoUSD: 0,
  });

  const [
    periodoSemana,
    setPeriodoSemana,
  ] = useState({
    desde: "",
    hasta: "",
  });

  /* =======================================================
     CARGA DASHBOARD
     ======================================================= */

  const cargarDashboard =
    useCallback(
      async () => {
        setCargando(true);
        setError("");

        try {
          const [
            pendientesData,
            semanaData,
            liquidacionesData,
            pagosData,
          ] =
            await Promise.all([
              getPendientesComisiones({
                sede: sedeDashboard,
              }),

              getResumenSemanalComisiones({
                sede: sedeDashboard,
              }),

              getLiquidacionesComisiones({
                sede: sedeDashboard,
              }),

              getPagosComisiones({
                sede: sedeDashboard,
                estado: "ACTIVO",
              }),
          ]);

          setPendientesValidar(
            Number(
              pendientesData
                ?.cantidad ||
                0
            )
          );

          setSemanaPendienteUSD(
            Number(
              semanaData
                ?.totales
                ?.totalSemanaUSD ||
                0
            )
          );

          setPeriodoSemana({
            desde:
              semanaData
                ?.periodo
                ?.desde ||
              "",

            hasta:
              semanaData
                ?.periodo
                ?.hasta ||
              "",
          });

          setLiquidaciones(
            Array.isArray(
              liquidacionesData
                ?.liquidaciones
            )
              ? liquidacionesData
                  .liquidaciones
              : []
          );

          setResumenLiquidaciones({
            cantidad:
              Number(
                liquidacionesData
                  ?.resumen
                  ?.cantidad ||
                  0
              ),

            totalLiquidadoUSD:
              Number(
                liquidacionesData
                  ?.resumen
                  ?.totalLiquidadoUSD ||
                  0
              ),

            totalPagadoUSD:
              Number(
                liquidacionesData
                  ?.resumen
                  ?.totalPagadoUSD ||
                  0
              ),

            saldoPendienteUSD:
              Number(
                liquidacionesData
                  ?.resumen
                  ?.saldoPendienteUSD ||
                  0
              ),
          });

          setResumenPagos({
            cantidadPagos:
              Number(
                pagosData
                  ?.resumen
                  ?.cantidadPagos ||
                  0
              ),

            totalPagadoUSD:
              Number(
                pagosData
                  ?.resumen
                  ?.totalPagadoUSD ||
                  0
              ),
          });
        } catch (err) {
          console.error(
            "Error cargando dashboard de comisiones:",
            err
          );

          setError(
            err?.response?.data
              ?.error ||
              err?.response?.data
                ?.message ||
              err?.message ||
              "No se pudo cargar el dashboard de comisiones."
          );
        } finally {
          setCargando(false);
        }
      },
      [sedeDashboard]
    );

  useEffect(() => {
    cargarDashboard();
  }, [cargarDashboard]);

  /* =======================================================
     CÁLCULOS
     ======================================================= */

  const liquidacionesPendientes =
    useMemo(
      () =>
        liquidaciones.filter(
          (item) =>
            Number(
              item
                ?.saldoPendienteUSD ||
                0
            ) > 0 &&
            String(
              item?.estado || ""
            ).toUpperCase() !==
              "ANULADA"
        ).length,
      [liquidaciones]
    );

  const vendedoresConSaldo =
    useMemo(() => {
      const ids =
        new Set();

      liquidaciones.forEach(
        (item) => {
          if (
            Number(
              item
                ?.saldoPendienteUSD ||
                0
            ) <= 0
          ) {
            return;
          }

          const id =
            item?.vendedor?._id ||
            item?.vendedor ||
            "";

          if (id) {
            ids.add(
              String(id)
            );
          }
        }
      );

      return ids.size;
    }, [liquidaciones]);

  const ultimasLiquidaciones =
    useMemo(
      () =>
        liquidaciones.slice(
          0,
          5
        ),
      [liquidaciones]
    );

  /* =======================================================
     VISTA
     ======================================================= */

  return (
    <main className="homealtas-riders">

      {/* HERO */}

      <section className="homealtas-hero">
        <div className="homealtas-divider" />

        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">

          <div>
            <h1>
              Comisiones
            </h1>

            <p>
              Administración,
              liquidación, pagos,
              cuenta corriente e
              informes de comisiones
              de vendedores.
            </p>
          </div>

          <div className="d-flex align-items-end gap-2">

            <div>
              <Form.Label className="small mb-1">
                Sede
              </Form.Label>

              <Form.Select
                size="sm"
                value={sedeDashboard}
                onChange={(e) =>
                  setSedeDashboard(
                    e.target.value
                  )
                }
                style={{
                  minWidth: "170px",
                }}
              >
                <option value="">
                  Todas
                </option>

                <option value="TUCUMAN">
                  Tucumán
                </option>

                <option value="YERBA_BUENA">
                  Yerba Buena
                </option>

                <option value="MIAMI">
                  Miami
                </option>
              </Form.Select>
            </div>

            <Button
              size="sm"
              variant="outline-secondary"
              onClick={cargarDashboard}
              disabled={cargando}
            >
              {cargando
                ? "Actualizando..."
                : "Actualizar"}
            </Button>

          </div>

        </div>
      </section>

      {/* ERROR */}

      {error && (
        <div className="alert alert-danger py-2">
          {error}
        </div>
      )}

      {/* ===================================================
          INDICADORES
          =================================================== */}

      <section className="mb-4">

        <div className="d-flex justify-content-between align-items-center mb-2">

          <div>
            <h5 className="mb-0">
              Estado general
            </h5>

            <small className="text-muted">
              Situación actual del
              circuito de comisiones
            </small>
          </div>

          {cargando && (
            <Spinner
              animation="border"
              size="sm"
            />
          )}

        </div>

        <Row className="g-2">

          <Col
            xs={12}
            sm={6}
            xl={2}
          >
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="py-3">

                <div className="small text-muted">
                  Pendientes de validar
                </div>

                <div className="fs-4 fw-bold">
                  {pendientesValidar}
                </div>

              </Card.Body>
            </Card>
          </Col>

          <Col
            xs={12}
            sm={6}
            xl={2}
          >
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="py-3">

                <div className="small text-muted">
                  Semana pendiente
                </div>

                <div className="fw-bold">
                  {formatoUSD(
                    semanaPendienteUSD
                  )}
                </div>

                <small className="text-muted">
                  {formatoFecha(
                    periodoSemana.desde
                  )}
                  {" → "}
                  {formatoFecha(
                    periodoSemana.hasta
                  )}
                </small>

              </Card.Body>
            </Card>
          </Col>

          <Col
            xs={12}
            sm={6}
            xl={2}
          >
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="py-3">

                <div className="small text-muted">
                  Total liquidado
                </div>

                <div className="fw-bold">
                  {formatoUSD(
                    resumenLiquidaciones
                      .totalLiquidadoUSD
                  )}
                </div>

                <small className="text-muted">
                  {
                    resumenLiquidaciones
                      .cantidad
                  }{" "}
                  liquidaciones
                </small>

              </Card.Body>
            </Card>
          </Col>

          <Col
            xs={12}
            sm={6}
            xl={2}
          >
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="py-3">

                <div className="small text-muted">
                  Total pagado
                </div>

                <div className="fw-bold text-success">
                  {formatoUSD(
                    resumenPagos
                      .totalPagadoUSD
                  )}
                </div>

                <small className="text-muted">
                  {
                    resumenPagos
                      .cantidadPagos
                  }{" "}
                  pagos
                </small>

              </Card.Body>
            </Card>
          </Col>

          <Col
            xs={12}
            sm={6}
            xl={2}
          >
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="py-3">

                <div className="small text-muted">
                  Saldo pendiente
                </div>

                <div className="fw-bold text-warning">
                  {formatoUSD(
                    resumenLiquidaciones
                      .saldoPendienteUSD
                  )}
                </div>

                <small className="text-muted">
                  {
                    liquidacionesPendientes
                  }{" "}
                  liquidaciones
                </small>

              </Card.Body>
            </Card>
          </Col>

          <Col
            xs={12}
            sm={6}
            xl={2}
          >
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="py-3">

                <div className="small text-muted">
                  Vendedores con saldo
                </div>

                <div className="fs-4 fw-bold">
                  {
                    vendedoresConSaldo
                  }
                </div>

              </Card.Body>
            </Card>
          </Col>

        </Row>
      </section>

      {/* ===================================================
          ÚLTIMAS LIQUIDACIONES
          =================================================== */}

      <section className="mb-4">

        <Card className="border-0 shadow-sm">

          <Card.Body>

            <div className="d-flex justify-content-between align-items-center mb-2">

              <div>
                <h5 className="mb-0">
                  Últimas liquidaciones
                </h5>

                <small className="text-muted">
                  Últimos cierres
                  registrados
                </small>
              </div>

              <Link
                to="/informe-comisiones-liquidaciones"
                className="btn btn-sm btn-outline-primary"
              >
                Ver informe
              </Link>

            </div>

            <Table
              responsive
              hover
              size="sm"
              className="align-middle mb-0"
            >

              <thead>
                <tr>
                  <th>
                    Vendedor
                  </th>

                  <th>
                    Sede
                  </th>

                  <th>
                    Tipo
                  </th>

                  <th>
                    Período
                  </th>

                  <th className="text-end">
                    Total
                  </th>

                  <th className="text-end">
                    Saldo
                  </th>

                  <th className="text-center">
                    Estado
                  </th>
                </tr>
              </thead>

              <tbody>

                {ultimasLiquidaciones.length ===
                0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center text-muted py-3"
                    >
                      No hay liquidaciones
                      para mostrar.
                    </td>
                  </tr>
                ) : (
                  ultimasLiquidaciones.map(
                    (item) => {
                      const estado =
                        String(
                          item?.estado ||
                            ""
                        ).toUpperCase();

                      return (
                        <tr
                          key={
                            item._id
                          }
                        >
                          <td className="fw-semibold">
                            {nombreVendedor(
                              item
                            )}
                          </td>

                          <td>
                            {item.sede}
                          </td>

                          <td>
                            <Badge
                              bg={
                                item.tipo ===
                                "MENSUAL"
                                  ? "info"
                                  : "secondary"
                              }
                            >
                              {item.tipo ===
                              "MENSUAL"
                                ? "Mensual"
                                : "Semanal"}
                            </Badge>
                          </td>

                          <td>
                            {formatoFecha(
                              item.periodoDesde
                            )}
                            {" → "}
                            {formatoFecha(
                              item.periodoHasta
                            )}
                          </td>

                          <td className="text-end fw-bold">
                            {formatoUSD(
                              item
                                .totalLiquidacionUSD
                            )}
                          </td>

                          <td className="text-end">
                            {formatoUSD(
                              item
                                .saldoPendienteUSD
                            )}
                          </td>

                          <td className="text-center">
                            <Badge
                              bg={
                                estado ===
                                "PAGADA"
                                  ? "success"
                                  : estado ===
                                    "PARCIAL"
                                  ? "info"
                                  : estado ===
                                    "ANULADA"
                                  ? "danger"
                                  : "warning"
                              }
                            >
                              {estado ===
                              "PAGADA"
                                ? "Pagada"
                                : estado ===
                                  "PARCIAL"
                                ? "Parcial"
                                : estado ===
                                  "ANULADA"
                                ? "Anulada"
                                : "Pendiente"}
                            </Badge>
                          </td>
                        </tr>
                      );
                    }
                  )
                )}

              </tbody>

            </Table>

          </Card.Body>

        </Card>

      </section>

      {/* ===================================================
          ACCESOS
          =================================================== */}

      <section>

        <div className="mb-2">
          <h5 className="mb-0">
            Accesos
          </h5>

          <small className="text-muted">
            Administración e
            informes del módulo
          </small>
        </div>

        <section className="homealtas-grid">

          <Link
            to="/administrador-comisiones"
            className="homealtas-card"
          >
            <div className="homealtas-icon-box">
              <Icon type="administracion" />
            </div>

            <div className="homealtas-card-body">
              <h2>
                Administración de
                Comisiones
              </h2>

              <p>
                Configuración,
                productos, bonos,
                validaciones,
                liquidaciones, pagos
                y cuenta corriente.
              </p>
            </div>
          </Link>

          <Link
            to="/informe-comisiones-liquidaciones"
            className="homealtas-card"
          >
            <div className="homealtas-icon-box">
              <Icon type="informes" />
            </div>

            <div className="homealtas-card-body">
              <h2>
                Liquidaciones de
                Comisiones
              </h2>

              <p>
                Liquidaciones
                semanales y mensuales,
                pagado, saldo
                pendiente y
                exportaciones.
              </p>
            </div>
          </Link>

          <Link
            to="/informe-comisiones-pagos"
            className="homealtas-card"
          >
            <div className="homealtas-icon-box">
              <Icon type="informes" />
            </div>

            <div className="homealtas-card-body">
              <h2>
                Pagos de Comisiones
              </h2>

              <p>
                Historial de pagos
                por vendedor, sede y
                período, con PDF y
                CSV.
              </p>
            </div>
          </Link>

          <Link
            to="/informe-comisiones-saldos"
            className="homealtas-card"
          >
            <div className="homealtas-icon-box">
              <Icon type="informes" />
            </div>

            <div className="homealtas-card-body">
              <h2>
                Saldos Pendientes
              </h2>

              <p>
                Liquidaciones
                pendientes, montos
                pagados y saldo
                adeudado.
              </p>
            </div>
          </Link>

          <Link
            to="/informe-comisiones-vendedores"
            className="homealtas-card"
          >
            <div className="homealtas-icon-box">
              <Icon type="informes" />
            </div>

            <div className="homealtas-card-body">
              <h2>
                Resumen por Vendedor
              </h2>

              <p>
                Comisiones, bonos,
                total generado, pagos
                y saldo por vendedor.
              </p>
            </div>
          </Link>

          <Link
            to="/informe-comisiones-detalle"
            className="homealtas-card"
          >
            <div className="homealtas-icon-box">
              <Icon type="informes" />
            </div>

            <div className="homealtas-card-body">
              <h2>
                Detalle por Venta
              </h2>

              <p>
                Auditoría por venta y
                producto, con precios,
                comisión y estado de
                liquidación.
              </p>
            </div>
          </Link>

        </section>

      </section>

    </main>
  );
};

export default HomeComisiones;